import * as React from "react"
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalHeader,
  InputGroup,
  InputLeftAddon,
  Button,
  useDisclosure,
  Box,
} from "@chakra-ui/react"
import { Form } from "./Form"
import { useForm } from "react-hook-form"
import { useMutation } from "react-query"
import { fetcher } from "../lib/fetcher"
import { useToast } from "../lib/hooks/useToast"
import { useOrgs } from "./providers/OrgProvider"
import { Select } from "./Select"
import { AiOutlineFileAdd } from "react-icons/ai"
import { BikeModelEntity } from "../lib/types/bikeModel"
import { getOrgUuidFromUrl } from "../lib/helpers/utils"
import { Input } from "./Input"
import { Textarea } from "./Textarea"
import { useFeatFlags } from "./providers/FeatFlagProvider"

interface BikeCreateResponse {
  name?: string
  serial_number?: string | null
  description?: string | null
  organization?: string
  model?: string | null
  lock?: string | null
  axa_lock?: string | null
  detail?: {
    non_field_errors: [
      {
        message: string
        code: string
      },
    ]
  }
}

interface BikeModelsResponse {
  count: number
  results: BikeModelEntity[]
}

interface Props {
  forceRefresh: () => void
}

export const BikeCreate: React.FC<Props> = (props) => {
  const form = useForm()
  const toast = useToast()
  const modalProps = useDisclosure()
  const { bike_sn_as_name_extension } = useFeatFlags()
  const { currentOrgs } = useOrgs()
  const organizationOptions = currentOrgs.map((org) => ({ label: org.name, value: org.url }))

  const [extended, setExtended] = React.useState(false)
  const [modelOptions, setModelOptions] = React.useState<{ label: string; value: string }[]>([])

  const { mutateAsync: create, isLoading } = useMutation<BikeCreateResponse, unknown, BikeCreateResponse>(
    (data) => fetcher("bicycles/", { body: JSON.stringify(data), method: "post" }),
  )

  const handleSubmit = async (values: BikeCreateResponse) => {
    try {
      const data: BikeCreateResponse = {
        name: values.name,
        organization: values.organization,
        serial_number: values.name,
        model:
          modelOptions[0] && modelOptions[0].value && modelOptions[0].value.length > 0
            ? modelOptions[0].value
            : null,
      }
      if (extended) {
        data.serial_number =
          !bike_sn_as_name_extension && values.serial_number && values.serial_number.length > 0
            ? values.serial_number
            : values.name
        data.description = values.description
        data.model = values.model && values.model.length > 0 ? values.model : null
      }
      const res = await create(data)
      if (res?.name && res?.organization) {
        toast({ description: "Asset created" })
        modalProps.onClose()
        form.reset()
        props.forceRefresh()
      } else if (res?.detail?.non_field_errors) {
        toast({ description: `Error: ${res.detail?.non_field_errors[0].message}`, status: "error" })
      } else {
        toast({ description: "Error creating asset", status: "error" })
      }
    } catch (error) {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const handleChangeOrg = async (url: string) => {
    const data: BikeModelsResponse = await fetcher(`bicycle_models/?organization=${getOrgUuidFromUrl(url)}`)
    const modelOpts = data?.results
      ? data.results.map((model) => ({ label: model.name, value: model.url }))
      : []
    setModelOptions(modelOpts)
  }

  React.useEffect(() => {
    handleChangeOrg(currentOrgs[0].url)
  }, [currentOrgs])

  return (
    <>
      <Button
        leftIcon={<AiOutlineFileAdd />}
        onClick={modalProps.onOpen}
        isDisabled={isLoading}
        isLoading={isLoading}
        variant="outline"
      >
        New asset
      </Button>
      <Modal {...modalProps} isCentered size="xl">
        <ModalOverlay zIndex={100}>
          <ModalContent>
            <ModalHeader pb={0}>Create new asset</ModalHeader>
            <ModalCloseButton />
            <ModalBody p={6}>
              <Form {...form} onSubmit={handleSubmit}>
                <InputGroup>
                  <InputLeftAddon w="130px" pl={3}>
                    {bike_sn_as_name_extension ? "Serial number" : "Name"}
                  </InputLeftAddon>
                  <Input name="name" isRequired borderTopLeftRadius={0} borderBottomLeftRadius={0} />
                </InputGroup>
                <InputGroup>
                  <InputLeftAddon w="130px">Organisation</InputLeftAddon>
                  <Select
                    borderTopLeftRadius={0}
                    borderBottomLeftRadius={0}
                    isRequired
                    name="organization"
                    isDisabled={organizationOptions.length <= 1}
                    options={organizationOptions}
                    onChange={(e) => handleChangeOrg(e.target.value)}
                  />
                </InputGroup>
                <Box
                  cursor="pointer"
                  pt={0}
                  color="blue.500"
                  fontSize="15px"
                  onClick={() => setExtended((extended) => !extended)}
                >
                  {extended ? "- Optional data" : "+ Optional data"}
                </Box>
                {extended && (
                  <>
                    {!bike_sn_as_name_extension && (
                      <InputGroup>
                        <InputLeftAddon w="130px" pl={3}>
                          Serial number
                        </InputLeftAddon>
                        <Input name="serial_number" borderTopLeftRadius={0} borderBottomLeftRadius={0} />
                      </InputGroup>
                    )}
                    <InputGroup>
                      <InputLeftAddon height="unset" w="130px" pl={3}>
                        Description
                      </InputLeftAddon>
                      <Textarea name="description" borderTopLeftRadius={0} borderBottomLeftRadius={0} />
                    </InputGroup>
                    <InputGroup>
                      <InputLeftAddon w="130px">Model</InputLeftAddon>
                      <Select
                        borderTopLeftRadius={0}
                        borderBottomLeftRadius={0}
                        name="model"
                        isDisabled={modelOptions.length < 1}
                        options={modelOptions}
                        placeholder={modelOptions.length < 1 ? "Unavailable" : "Choose one"}
                      />
                    </InputGroup>
                  </>
                )}
                <Button
                  width="100%"
                  colorScheme="green"
                  type="submit"
                  isDisabled={isLoading}
                  isLoading={isLoading}
                >
                  Create
                </Button>
              </Form>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </>
  )
}
